/**
 * Generic helper hooks for realtime Firestore subscriptions.
 * These reduce boilerplate when converting useQuery hooks to onSnapshot-based hooks.
 */

import { useState, useEffect } from "react";

/**
 * Hook for realtime list (collection) subscriptions.
 * @param subscribeFn - Factory that returns an unsubscribe function; null = disabled.
 * @param deps - React effect dependencies (the primitive IDs that feed into subscribeFn).
 */
export function useRealtimeList<T>(
  subscribeFn:
    | ((
        callback: (data: T[]) => void,
        onError?: (error: Error) => void,
      ) => () => void)
    | null,
  deps: unknown[],
) {
  const [data, setData] = useState<T[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!subscribeFn) {
      setData([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeFn(
      (items) => {
        setData(items);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error };
}

/**
 * Hook for realtime single-document subscriptions.
 * @param subscribeFn - Factory that returns an unsubscribe function; null = disabled.
 * @param deps - React effect dependencies.
 */
export function useRealtimeDoc<T>(
  subscribeFn:
    | ((
        callback: (data: T | null) => void,
        onError?: (error: Error) => void,
      ) => () => void)
    | null,
  deps: unknown[],
) {
  const [data, setData] = useState<T | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!subscribeFn) {
      setData(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeFn(
      (item) => {
        setData(item);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, isLoading, error };
}
